import { ReactNode } from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import type {
  CardAppearance,
  DragSourceContext,
  EventLayout,
  EventRenderContext,
  GridDragAndDropAdapter,
  TimeGridEntity,
  TimeGridEvent,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import { GridCard } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/components/GridCard'
import { GhostShiftCard } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/components/GhostShiftCard'

const CELL_BOTTOM_PADDING_PX = 28

interface Props<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent, TDragDrop> {
  entity: TEntity
  layouts: EventLayout<TEvent>[]
  paddingX: number
  paddingY: number
  cardLayout: {
    minHeight: number
    paddingX: number
    paddingY: number
    radius: number
  }
  minColumnWidth: number
  columnCount: number
  eventRenderer: (event: TEvent, context: EventRenderContext<TDragDrop>) => ReactNode
  cardAppearance?: (event: TEvent) => CardAppearance
  dragDropHandlers?: TDragDrop
  dragAndDropAdapter?: GridDragAndDropAdapter<TEntity, TEvent>
  selectedEventIds?: Set<string>
  isSingleSelectedEventGlobally?: boolean
  onEventSelected?: (
    eventId: string,
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
  ) => void
  onCopySelection?: () => void
  ghostCellSlotIndex?: number | null
  onGhostCardRef?: (node: HTMLDivElement | null) => void
}

const TimeGridRowEvents = <
  TEntity extends TimeGridEntity,
  TEvent extends TimeGridEvent,
  TDragDrop,
>({
  entity,
  layouts,
  paddingX,
  paddingY,
  cardLayout,
  minColumnWidth,
  columnCount,
  eventRenderer,
  cardAppearance,
  dragDropHandlers,
  dragAndDropAdapter,
  selectedEventIds,
  isSingleSelectedEventGlobally,
  onEventSelected,
  onCopySelection,
  ghostCellSlotIndex,
  onGhostCardRef,
}: Props<TEntity, TEvent, TDragDrop>) => {
  const maxStackCount = layouts.reduce((max, layout) => Math.max(max, layout.stackIndex + 1), 0)
  const hasColumnOverlap = (left: EventLayout<TEvent>, right: EventLayout<TEvent>): boolean => {
    return (
      left.span.startIndex <= right.span.endIndex && left.span.endIndex >= right.span.startIndex
    )
  }

  const getFillHeightRowSpan = (layout: EventLayout<TEvent>): number => {
    // A fill-height card can grow down until the next stacked event that overlaps in columns.
    const closestOverlappingStackAbove = layouts
      .filter((candidate) => {
        if (candidate.event.id === layout.event.id) return false
        if (candidate.stackIndex <= layout.stackIndex) return false

        return hasColumnOverlap(layout, candidate)
      })
      .reduce<number | null>((closest, candidate) => {
        if (closest === null) return candidate.stackIndex

        return Math.min(closest, candidate.stackIndex)
      }, null)

    if (closestOverlappingStackAbove === null) {
      return Math.max(1, maxStackCount - layout.stackIndex)
    }

    return Math.max(1, closestOverlappingStackAbove - layout.stackIndex)
  }

  return (
    <Box
      style={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: `repeat(${columnCount}, minmax(${minColumnWidth}px, 1fr))`,
        padding: `${paddingY}px 0 ${CELL_BOTTOM_PADDING_PX}px 0`,
        boxSizing: 'border-box',
      }}
    >
      {/* Events are placed in a CSS grid so row height grows with stacked items. */}
      {layouts.map((layout) => {
        const spanWidth = layout.span.endIndex - layout.span.startIndex + 1
        const gridColumn = `${layout.span.startIndex + 1} / span ${spanWidth}`
        const appearance = cardAppearance?.(layout.event)
        const isSelectableEvent = layout.event.selectable !== false
        const fillHeightRowSpan = getFillHeightRowSpan(layout)
        const gridRow = appearance?.fillHeight
          ? `${layout.stackIndex + 1} / span ${fillHeightRowSpan}`
          : `${layout.stackIndex + 1}`
        const isEventSelected = selectedEventIds?.has(layout.event.id) ?? false
        const isSingleSelectedEvent = isEventSelected && !!isSingleSelectedEventGlobally
        const dragSourceContext: DragSourceContext<TEntity, TEvent> = {
          entity,
          event: layout.event,
          span: layout.span,
          stackIndex: layout.stackIndex,
          clampedRange: layout.clampedRange,
        }
        const dragItem = dragAndDropAdapter?.getDragItem(dragSourceContext) ?? undefined
        const canDrag = !!dragItem && (dragAndDropAdapter?.canDrag?.(dragSourceContext) ?? true)

        return (
          <GridCard
            key={layout.event.id}
            gridColumn={gridColumn}
            gridRow={gridRow}
            minHeight={cardLayout.minHeight}
            paddingX={paddingX}
            cardPaddingX={cardLayout.paddingX}
            cardPaddingY={cardLayout.paddingY}
            radius={cardLayout.radius}
            appearance={appearance}
            isSelected={isEventSelected}
            dragItem={dragItem}
            canDrag={canDrag}
            onEventSelected={isSelectableEvent ? onEventSelected : undefined}
            eventId={layout.event.id}
          >
            {eventRenderer(layout.event, {
              span: layout.span,
              stackIndex: layout.stackIndex,
              clampedRange: layout.clampedRange,
              dragDropHandlers,
              selected: isSingleSelectedEvent,
              onCopy: onCopySelection,
            })}
          </GridCard>
        )
      })}
      {(() => {
        if (ghostCellSlotIndex == null) return null

        // Place ghost card below any existing cards in the same column
        const columnMaxStack = layouts.reduce((max, layout) => {
          if (
            layout.span.startIndex <= ghostCellSlotIndex &&
            layout.span.endIndex >= ghostCellSlotIndex
          ) {
            return Math.max(max, layout.stackIndex + 1)
          }
          return max
        }, 0)

        return (
          <Box
            ref={onGhostCardRef}
            style={{
              gridColumn: `${ghostCellSlotIndex + 1}`,
              gridRow: `${columnMaxStack + 1}`,
              padding: `0 ${paddingX}px`,
              boxSizing: 'border-box',
            }}
          >
            <GhostShiftCard />
          </Box>
        )
      })()}
    </Box>
  )
}

export { TimeGridRowEvents }
